import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import PageHeader from './PageHeader';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import { speakWithAvatar, stopAvatarSpeech, isAvatarSpeaking } from '../ttsPlayer';
import { startSTT, stopSTT } from '../sttClient';
import { getUserInitials } from '../utils/userUtils';
import './Common.css';
import './CulturalInsights.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';

const CulturalInsightsChat = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language: initLang = 'de', country = 'Germany', flagUrl: initFlagUrl = '' } = location.state || {};
  const [language, setLanguage] = useState(initLang);
  const [inputText, setInputText] = useState('');
  const [messages, setMessages] = useState([]);
  const [showCategoriesPanel, setShowCategoriesPanel] = useState(false);
  const [showCollapseTooltip, setShowCollapseTooltip] = useState(false);
  const [detailPanel, setDetailPanel] = useState(null); // { topic, content }
  const [showPromptsPopup, setShowPromptsPopup] = useState(false);
  const [categoryPrompts, setCategoryPrompts] = useState([]);
  const [agentLoading, setAgentLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [frontendSessionId] = useState(() => 'ci_fe_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
  const [exploreData, setExploreData] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentSubcategory, setCurrentSubcategory] = useState(null);
  const [activeCountry, setActiveCountry] = useState(country);
  const [activeFlag, setActiveFlag] = useState(initFlagUrl || '/images/germany_flag.png');
  const [isReading, setIsReading] = useState(false);
  const [readingId, setReadingId] = useState(null);
  const [inlineDisclaimerChecked, setInlineDisclaimerChecked] = useState(false);
  const [sttActive, setSttActive] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState('');
  const [partialTranscript, setPartialTranscript] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const latestBotRef = useRef(null);
  const categoriesPanelRef = useRef(null);
  const detailClosedRef = useRef(false);

  usePageTimeTracker('cultural-insights-chat', user);

  // Cleanup STT on unmount (page navigation)
  useEffect(() => {
    return () => { stopSTT(); };
  }, []);

  // Call agent on mount for "Explore All" or category click
  useEffect(() => {
    const { category } = location.state || {};
    const hasLocationState = !!location.state;
    
    if (!hasLocationState) {
      // Direct access / hard refresh — redirect to landing page
      navigate('/cultural-insights', { replace: true });
      return;
    }

    // Coming from landing page — disclaimer already accepted there, proceed
    if (category) {
      setCurrentCategory(category);
      callAgent('category', category, true);
    } else {
      callAgent('explore', null);
    }
  }, []);

  // Close categories panel on outside click
  useEffect(() => {
    if (!showCategoriesPanel) return;
    const handleClick = (e) => {
      if (categoriesPanelRef.current && !categoriesPanelRef.current.contains(e.target)) {
        setShowCategoriesPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCategoriesPanel]);

  const breadcrumbs = [
    { label: 'Home', path: '/', current: false },
    { label: 'Explore Learning', path: '/explore-learning', current: false },
    { label: 'Cultural Insights', current: true }
  ];

  const callAgent = (action, queryText, isInitialLoad = false) => {
    setAgentLoading(true);
    const payload = { action, country: activeCountry };
    if (action === 'category') payload.category = queryText;
    else if (action === 'detail') payload.prompt = queryText;
    else if (action === 'query') payload.prompt = queryText;
    if (sessionId) payload.session_id = sessionId;

    axios.post(`${API_BASE_URL}/cultural-insights`, payload, { withCredentials: true })
      .then(res => {
        setAgentLoading(false);
        if (res.data.success) {
          const data = res.data.data;
          if (data.session_id) setSessionId(data.session_id);
          console.log('CI Agent response:', data);
          
          if (action === 'explore') {
            // Use response to populate the explore section
            const content = data.content || data;
            setExploreData(content);
            // Save user action + bot response to history
            axios.post(`${API_BASE_URL}/cultural-insights/history/save`, {
              conversation_id: null,
              role: 'user',
              message_text: 'Explore All - ' + (content.country || country),
              intent: null,
              full_response: null,
              context_country: country,
              context_category: 'Explore All',
              context_subcategory: null,
              session_id: frontendSessionId,
              country,
              category: 'Explore All',
              title: (content.country || country) + ' - Explore All'
            }, { withCredentials: true }).then(res => {
              if (res.data.success && res.data.conversation_id) {
                setConversationId(res.data.conversation_id);
                // Now save bot response
                axios.post(`${API_BASE_URL}/cultural-insights/history/save`, {
                  conversation_id: res.data.conversation_id,
                  role: 'bot',
                  message_text: content.welcome_message || 'Explore All',
                  intent: null,
                  full_response: JSON.stringify(content),
                  context_country: country,
                  context_category: 'Explore All',
                  context_subcategory: null,
                  session_id: frontendSessionId,
                  country,
                  category: 'Explore All',
                  title: (content.country || country) + ' - Explore All'
                }, { withCredentials: true }).catch(() => {});
              }
            }).catch(() => {});
          } else if (action === 'category' && isInitialLoad) {
            // Initial category load — show in explore format with topic rows
            const content = data.content || data;
            setExploreData({
              country: content.country || country,
              country_info: content.country_info || { capital: '', language: '', timezone: '', currency: '' },
              welcome_message: content.category_description || '',
              categories: null,
              subcategories: content.subcategories || [],
              category: content.category || currentCategory
            });
            // Save to history - user action first, then bot response
            axios.post(`${API_BASE_URL}/cultural-insights/history/save`, {
              conversation_id: null,
              role: 'user',
              message_text: queryText,
              intent: null,
              full_response: null,
              context_country: country,
              context_category: queryText,
              context_subcategory: null,
              session_id: frontendSessionId,
              country,
              category: queryText,
              title: (content.country || country) + ' - ' + queryText
            }, { withCredentials: true }).then(res => {
              if (res.data.success && res.data.conversation_id) {
                setConversationId(res.data.conversation_id);
                axios.post(`${API_BASE_URL}/cultural-insights/history/save`, {
                  conversation_id: res.data.conversation_id,
                  role: 'bot',
                  message_text: content.category_description || queryText,
                  intent: null,
                  full_response: JSON.stringify(content),
                  context_country: country,
                  context_category: queryText,
                  context_subcategory: null,
                  session_id: frontendSessionId,
                  country,
                  category: queryText,
                  title: (content.country || country) + ' - ' + queryText
                }, { withCredentials: true }).catch(() => {});
              }
            }).catch(() => {});
          } else {
            // Remove loading message and add bot response (category/query)
            const content = data.content || data;
            const botText = content.category_description || content.summary || content.response || content.welcome_message || '';
            setMessages(prev => {
              const filtered = prev.filter(m => !m.loading);
              return [...filtered, {
                role: 'bot',
                summary: botText,
                subtitle: content.subtitle || '',
                topics: content.subcategories || content.topics || [],
                msgCountry: activeCountry,
                msgCategory: queryText || currentCategory
              }];
            });
            // Save to history
            if (queryText) {
              saveToHistory('user', queryText, null).then(convId => {
                saveToHistory('bot', botText, null, convId, content);
              });
            }
            setTimeout(() => {
              if (latestBotRef.current) latestBotRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
          }
        }
      })
      .catch(err => {
        setAgentLoading(false);
        console.error('CI Agent error:', err);
        if (action !== 'explore') {
          // Remove loading and show error
          setMessages(prev => {
            const filtered = prev.filter(m => !m.loading);
            return [...filtered, {
              role: 'bot',
              summary: 'There was a problem processing your request. Please try again later or let me know if I can help.',
              topics: []
            }];
          });
        }
      });
  };

  const saveToHistory = (role, text, intent, existingConvId, fullResponse) => {
    const convId = existingConvId || conversationId;
    return axios.post(`${API_BASE_URL}/cultural-insights/history/save`, {
      conversation_id: convId,
      role,
      message_text: text,
      intent: intent || null,
      full_response: fullResponse ? JSON.stringify(fullResponse) : null,
      context_country: country,
      context_category: currentCategory,
      context_subcategory: currentSubcategory,
      session_id: frontendSessionId,
      country,
      category: currentCategory,
      title: currentCategory || country || 'Cultural Insights'
    }, { withCredentials: true })
      .then(res => {
        if (res.data.success && res.data.conversation_id) {
          if (!conversationId) setConversationId(res.data.conversation_id);
          return res.data.conversation_id;
        }
        return convId;
      })
      .catch(err => { console.error('Save history error:', err); return convId; });
  };

  // TTS Read Aloud
  const handleReadAloud = async (text, id) => {
    if (isReading && readingId === id) {
      stopAvatarSpeech();
      window.speechSynthesis.cancel();
      setIsReading(false);
      setReadingId(null);
      return;
    }
    stopAvatarSpeech();
    window.speechSynthesis.cancel();
    if (!text) return;
    setIsReading(true);
    setReadingId(id);
    try {
      await speakWithAvatar(text, null, 'Hans');
    } catch (e) {
      console.error('TTS error:', e);
      setIsReading(false);
      setReadingId(null);
    }
  };

  // Check TTS completion
  useEffect(() => {
    if (!isReading) return;
    // Wait a bit before starting to check (give TTS time to start)
    const timeout = setTimeout(() => {
      const check = setInterval(() => {
        if (!isAvatarSpeaking() && !window.speechSynthesis.speaking) {
          setIsReading(false);
          setReadingId(null);
          clearInterval(check);
        }
      }, 1000);
      return () => clearInterval(check);
    }, 2000);
    return () => clearTimeout(timeout);
  }, [isReading]);

  // STT Mic handler
  const handleMicClick = () => {
    if (sttActive) {
      stopSTT();
      setSttActive(false);
      // Keep the current inputText as-is
      setFinalTranscript('');
      setPartialTranscript('');
    } else {
      // Start recording — keep existing text, append new speech
      setFinalTranscript(inputText);
      setPartialTranscript('');
      setSttActive(true);
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/stt`;
      startSTT(
        wsUrl,
        { language: 'en-US' },
        ({ text, isPartial }) => {
          if (isPartial) {
            setPartialTranscript(text);
          } else {
            setFinalTranscript(prev => (prev + ' ' + text).trim());
            setPartialTranscript('');
          }
        }
      );
    }
  };

  // Update inputText when transcript changes and auto-grow textarea
  useEffect(() => {
    if (sttActive) {
      const combinedText = [finalTranscript, partialTranscript].filter(Boolean).join(' ');
      setInputText(combinedText);
      // Auto-grow textarea
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.style.height = 'auto';
          const scrollH = inputRef.current.scrollHeight;
          inputRef.current.style.height = Math.min(scrollH, 120) + 'px';
          inputRef.current.style.overflowY = scrollH > 120 ? 'auto' : 'hidden';
          // Scroll to bottom of textarea to show latest text
          inputRef.current.scrollTop = inputRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [finalTranscript, partialTranscript, sttActive]);

  const getCountryFlag = () => {
    return activeFlag;
  };

  const getLanguageName = () => {
    const map = { 'de': 'German', 'en': 'English', 'fr': 'French', 'es': 'Spanish', 'it': 'Italian', 'ja': 'Japanese' };
    return map[language?.toLowerCase()] || 'German';
  };

  // Placeholder data — replaced by agent response when available
  const countryData = exploreData?.country_info || {
    capital: 'Berlin',
    language: 'Deutsch',
    timezone: 'Central European Time (CET)',
    currency: 'Euro (€)'
  };

  const welcomeText = exploreData?.welcome_message || `Welcome! I'm here to guide you through the cultural insights of ${country}, helping you understand its communication style, workplace culture, traditions, and everyday social practices through engaging and interactive learning.`;
  const summaryText = exploreData ? '' : `${getLanguageName()} culture reflects a balance of tradition and modernity, shaped by values of discipline, efficiency, community, art, and rich cultural heritage.`;

  const getCategoryIcon = (name) => {
    const iconMap = {
      'Communication Style': '/images/communicationCI.svg',
      'Workplace Culture': '/images/workplaceCI.svg',
      'Social Etiquette': '/images/socialci.svg',
      'Traditions and Celebrations': '/images/traditionalCI.svg',
      'Civic Behaviour': '/images/civicCI.svg',
      'Civic Behavior': '/images/civicCI.svg'
    };
    return iconMap[name] || '/images/communicationCI.svg';
  };

  const getPurpleCategoryIcon = (name) => {
    const iconMap = {
      'Communication Style': '/images/comm.svg',
      'Workplace Culture': '/images/work.svg',
      'Social Etiquette': '/images/social.svg',
      'Traditions and Celebrations': '/images/tradition.svg',
      'Civic Behaviour': '/images/Civic.svg',
      'Civic Behavior': '/images/Civic.svg'
    };
    return iconMap[name] || '/images/comm.svg';
  };

  const categories = exploreData?.categories
    ? exploreData.categories.map(c => ({ title: c.name, description: c.description, icon: getCategoryIcon(c.name) }))
    : [
      { title: 'Communication Style', description: 'How people communicate ideas, feedback, and expectations in professional and social interactions.', icon: '/images/communicationCI.svg' },
      { title: 'Workplace Culture', description: 'Shared values, behaviors, and expectations that shape how people work and collaborate.', icon: '/images/workplaceCI.svg' },
      { title: 'Social Etiquette', description: 'Culturally appropriate behaviors and manners followed in professional and social settings.', icon: '/images/socialci.svg' },
      { title: 'Traditions and Celebrations', description: 'Customs, festivals, and cultural practices that reflect a country\'s heritage and identity.', icon: '/images/traditionalCI.svg' },
      { title: 'Civic Behaviour', description: 'Expected societal and professional conduct that reflects responsibility, respect, and ethical behavior.', icon: '/images/civicCI.svg' }
    ];

  const handleSend = () => {
    if (!inputText.trim() || waitingForResponse) return;
    // Stop STT if active
    if (sttActive) { stopSTT(); setSttActive(false); setFinalTranscript(''); setPartialTranscript(''); }
    // Stop TTS if reading
    if (isReading) { stopAvatarSpeech(); window.speechSynthesis.cancel(); setIsReading(false); setReadingId(null); }
    const msg = inputText.trim();
    setInputText('');
    setShowPromptsPopup(false);
    if (inputRef.current) inputRef.current.style.height = 'auto';
    
    // Add user message and loading
    setMessages(prev => [...prev, { role: 'user', text: msg }, { role: 'bot', loading: true }]);
    setWaitingForResponse(true);
    setTimeout(() => {
      if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, 100);

    // Hit conversation endpoint
    axios.post(`${API_BASE_URL}/cultural-insights/conversation`, {
      session_id: sessionId || '',
      country: activeCountry,
      category: currentCategory || 'null',
      subcategory: currentSubcategory || 'null',
      message: msg
    }, { withCredentials: true })
      .then(res => {
        if (res.data.success) {
          const data = res.data.data;
          const content = data.content || data;
          console.log('CI Conversation response:', content);
          if (content.session_id) setSessionId(content.session_id);
          if (content.current_context?.category) setCurrentCategory(content.current_context.category);
          if (content.current_context?.subcategory) setCurrentSubcategory(content.current_context.subcategory);

          // Handle intent-based responses
          if (content.intent === 'change_country' && content.new_content) {
            const nc = content.new_content;
            const pendingExplore = {
              country: nc.country,
              country_info: nc.country_info || {},
              welcome_message: nc.welcome_message || nc.category_description || '',
              categories: nc.categories || null,
              subcategories: nc.subcategories || [],
              category: nc.category || null
            };
            // Check disclaimer for new country
            axios.get(`${API_BASE_URL}/cultural-insights/disclaimer/check?country=${encodeURIComponent(nc.country)}&_t=${Date.now()}`, { withCredentials: true })
              .then(dRes => {
                if (dRes.data.success && dRes.data.accepted) {
                  // Already accepted today — fetch flag first, then show inline
                  setCurrentCategory(pendingExplore.category || null);
                  setActiveCountry(nc.country);
                  axios.get(`${API_BASE_URL}/cultural-insights/countries`, { withCredentials: true })
                    .then(cRes => {
                      let flagUrl = activeFlag;
                      if (cRes.data.success) {
                        const found = cRes.data.countries.find(c => c.country_name === nc.country);
                        if (found?.flag_url) { flagUrl = found.flag_url; setActiveFlag(found.flag_url); }
                      }
                      setMessages(prev => {
                        const filtered = prev.filter(m => !m.loading);
                        return [...filtered, { 
                          role: 'bot', 
                          isCountryChange: true,
                          countryData: pendingExplore,
                          summary: pendingExplore.welcome_message || content.response || '',
                          topics: pendingExplore.subcategories || [],
                          msgCountry: nc.country,
                          msgCategory: pendingExplore.category || null,
                          flagUrl: flagUrl
                        }];
                      });
                    }).catch(() => {
                      setMessages(prev => {
                        const filtered = prev.filter(m => !m.loading);
                        return [...filtered, { 
                          role: 'bot', 
                          isCountryChange: true,
                          countryData: pendingExplore,
                          summary: pendingExplore.welcome_message || content.response || '',
                          topics: pendingExplore.subcategories || [],
                          msgCountry: nc.country,
                          msgCategory: pendingExplore.category || null,
                          flagUrl: activeFlag
                        }];
                      });
                    });
                } else {
                  // Show inline disclaimer ONLY (no bot response yet)
                  setMessages(prev => {
                    const filtered = prev.filter(m => !m.loading);
                    return [...filtered, { role: 'disclaimer', country: nc.country, pendingExplore, response: content.response }];
                  });
                }
              })
              .catch(() => {
                setMessages(prev => {
                  const filtered = prev.filter(m => !m.loading);
                  return [...filtered, { role: 'disclaimer', country: nc.country, pendingExplore, response: content.response }];
                });
              });
          } else if (content.intent === 'change_category' && content.new_content) {
            const nc = content.new_content;
            setCurrentCategory(nc.category);
            setMessages(prev => {
              const filtered = prev.filter(m => !m.loading);
              return [...filtered, {
                role: 'bot',
                summary: content.response || nc.category_description || '',
                topics: nc.subcategories || []
              }];
            });
          } else if (content.intent === 'change_subcategory' && content.new_content) {
            const nc = content.new_content;
            setCurrentSubcategory(nc.subcategory || nc.title);
            setDetailPanel({
              topic: nc.title || nc.subcategory,
              description: nc.description || '',
              tips: nc.tips || null,
              example: nc.example || null,
              image: nc.image_url || null,
              loading: false
            });
            setMessages(prev => {
              const filtered = prev.filter(m => !m.loading);
              return [...filtered, { role: 'bot', summary: content.response || '', topics: [] }];
            });
          } else {
            // just_answer or no intent
            setMessages(prev => {
              const filtered = prev.filter(m => !m.loading);
              return [...filtered, { role: 'bot', summary: content.response || content.summary || '', topics: [] }];
            });
          }

          setWaitingForResponse(false);
          // Save to history
          saveToHistory('user', msg, null).then(convId => {
            saveToHistory('bot', content.response || content.summary || '', content.intent || 'just_answer', convId, content);
          });
        }
        setTimeout(() => {
          if (latestBotRef.current) latestBotRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      })
      .catch(err => {
        console.error('CI Conversation error:', err);
        setWaitingForResponse(false);
        setMessages(prev => {
          const filtered = prev.filter(m => !m.loading);
          return [...filtered, { role: 'bot', summary: 'There was a problem processing your request. Please try again later or let me know if I can help.', topics: [] }];
        });
      });
  };

  const handleTogglePrompts = () => {
    if (showPromptsPopup) {
      setShowPromptsPopup(false);
      return;
    }
    // Fetch prompts from DB — filtered by category if one is selected
    const categoryMap = { 'Communication Style': 1, 'Workplace Culture': 2, 'Social Etiquette': 3, 'Traditions and Celebrations': 4, 'Civic Behaviour': 5 };
    const catId = currentCategory ? categoryMap[currentCategory] : null;
    const url = catId ? `${API_BASE_URL}/cultural-insights/prompts?category_id=${catId}` : `${API_BASE_URL}/cultural-insights/prompts`;
    axios.get(url, { withCredentials: true })
      .then(res => {
        if (res.data.success) setCategoryPrompts(res.data.prompts);
      })
      .catch(() => {
        setCategoryPrompts([]);
      });
    setShowPromptsPopup(true);
  };

  const handlePromptClick = (promptText) => {
    if (waitingForResponse) return;
    setShowPromptsPopup(false);
    // Stop STT if active
    if (sttActive) { stopSTT(); setSttActive(false); setFinalTranscript(''); setPartialTranscript(''); }
    // Stop TTS if reading
    if (isReading) { stopAvatarSpeech(); window.speechSynthesis.cancel(); setIsReading(false); setReadingId(null); }
    setInputText(promptText);
    // Trigger send with the prompt
    setMessages(prev => [...prev, { role: 'user', text: promptText }, { role: 'bot', loading: true }]);
    setWaitingForResponse(true);
    setTimeout(() => {
      if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, 100);

    axios.post(`${API_BASE_URL}/cultural-insights/conversation`, {
      session_id: sessionId || '',
      country: activeCountry,
      category: currentCategory || 'null',
      subcategory: currentSubcategory || 'null',
      message: promptText
    }, { withCredentials: true })
      .then(res => {
        if (res.data.success) {
          const data = res.data.data;
          const content = data.content || data;
          console.log('CI Prompt response:', content);
          if (content.session_id) setSessionId(content.session_id);
          if (content.current_context?.category) setCurrentCategory(content.current_context.category);
          if (content.current_context?.subcategory) setCurrentSubcategory(content.current_context.subcategory);
          setMessages(prev => {
            const filtered = prev.filter(m => !m.loading);
            return [...filtered, { role: 'bot', summary: content.response || content.summary || '', topics: [] }];
          });
          setWaitingForResponse(false);
          // Save to history
          saveToHistory('user', promptText, null).then(convId => {
            saveToHistory('bot', content.response || content.summary || '', content.intent || 'just_answer', convId, content);
          });
        }
        setTimeout(() => {
          if (latestBotRef.current) latestBotRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      })
      .catch(err => {
        console.error('CI Prompt error:', err);
        setWaitingForResponse(false);
        setMessages(prev => {
          const filtered = prev.filter(m => !m.loading);
          return [...filtered, { role: 'bot', summary: 'There was a problem processing your request. Please try again later or let me know if I can help.', topics: [] }];
        });
      });
    setInputText('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
  };

  const handleViewDetails = (subcategory, overrideCountry, overrideCategory) => {
    setCurrentSubcategory(subcategory);
    detailClosedRef.current = false;
    const detailCountry = overrideCountry || activeCountry;
    const detailCategory = overrideCategory || currentCategory;
    // Call agent for details with country, category, subcategory
    setDetailPanel({ topic: subcategory, description: '', example: null, loading: true });
    axios.post(`${API_BASE_URL}/cultural-insights`, { 
      action: 'details', 
      country: detailCountry, 
      category: detailCategory, 
      subcategory,
      session_id: sessionId 
    }, { withCredentials: true })
      .then(res => {
        if (detailClosedRef.current) return; // User closed panel, ignore response
        if (res.data.success) {
          const data = res.data.data;
          const content = data.content || data;
          console.log('CI Details response:', content);
          if (data.session_id) setSessionId(data.session_id);
          setDetailPanel({
            topic: subcategory,
            description: content.description || content.response || content.summary || 'No details available.',
            example: content.example || null,
            tips: content.tips || null,
            image: content.image || content.image_url || null,
            loading: false
          });
        }
      })
      .catch(() => {
        if (detailClosedRef.current) return;
        setDetailPanel({
          topic: subcategory,
          description: 'There was a problem processing your request. Please try again later or let me know if I can help.',
          example: null,
          tips: null,
          image: null,
          loading: false
        });
      });
  };

  const handleCategoryQuery = (queryText) => {
    setCurrentCategory(queryText);
    setMessages(prev => [...prev, { role: 'user', text: queryText }]);
    // Show typing indicator
    setMessages(prev => [...prev, { role: 'bot', loading: true }]);
    setTimeout(() => {
      if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    // Hit the agent
    callAgent('category', queryText);
  };

  return (
    <div className={`ci-page${showCategoriesPanel ? ' panel-open' : ''}`}>
      <Header user={user} />
      <div className="nav-header-container">
        <SideNav />
        <PageHeader breadcrumbs={breadcrumbs} language={language} proficiencyLevel="beginner-a1" onLanguageChange={setLanguage} onLevelChange={() => {}} rightContent={<></>} />
      </div>

      <main className={`ci-chat-content${detailPanel ? ' with-detail-panel' : ''}`}>
        <div className="ci-chat-messages">
          {agentLoading && !exploreData && (
            <div className="ci-loading-container">
              <div className="ci-bot-avatar"><span>LB</span></div>
              <div className="ci-typing"><span></span><span></span><span></span></div>
            </div>
          )}

          {exploreData && (
            <>
          <div className="ci-explore-header">
            <div className="ci-bot-avatar"><span>LB</span></div>
            <img src={initFlagUrl || '/images/germany_flag.png'} alt="" className="ci-explore-flag" />
            <h2 className="ci-explore-title">{exploreData?.country || country} Life: <span style={{fontWeight: 100, fontFamily: 'Graphik-Regular'}}>{exploreData?.category || 'Explore All'}</span></h2>
          </div>

          <div className="ci-explore-meta">
            <span>Capital: <strong>{countryData.capital}</strong></span>
            <span>Language(s): <strong>{countryData.language}</strong></span>
            <span>Time Zone: <strong>{countryData.timezone}</strong></span>
            <span>Currency: <strong>{countryData.currency}</strong></span>
          </div>

          <div className="ci-explore-divider"></div>

          <div className="ci-explore-body">
            <p className="ci-explore-welcome">{welcomeText}</p>
            {summaryText && <p className="ci-explore-summary">{summaryText}</p>}
            {exploreData?.categories && (
              <>
                <p className="ci-explore-select">Select a category to explore further -</p>
                <div className="ci-explore-cards">
                  {categories.map((cat, i) => (
                    <div key={i} className="ci-explore-card">
                      <img src={cat.icon} alt="" className="ci-explore-card-icon" />
                      <div className="ci-explore-card-body">
                        <p className="ci-explore-card-text"><strong>{cat.title}:</strong> {cat.description}</p>
                        <a className="ci-explore-card-link" onClick={() => handleCategoryQuery(cat.title)}>View details</a>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {exploreData?.subcategories && (
              <div className="ci-msg-bot-topics">
                {exploreData.subcategories.map((topic, ti) => (
                  <div key={ti} className={`ci-msg-topic-row${detailPanel?.topic === topic ? ' active' : ''}`}>
                    <span className="ci-msg-topic-name">{topic}</span>
                    <a className="ci-msg-topic-link" onClick={() => handleViewDetails(topic, country, exploreData?.category || currentCategory)}>View details</a>
                  </div>
                ))}
              </div>
            )}

            <div className="ci-explore-divider"></div>
            <div className="ci-say-aloud" onClick={() => {
              let fullText = welcomeText || '';
              if (summaryText) fullText += ' ' + summaryText;
              if (exploreData?.categories) {
                fullText += '. Categories: ' + exploreData.categories.map(c => c.name + ': ' + c.description).join('. ');
              }
              if (exploreData?.subcategories?.length > 0) {
                fullText += '. Topics: ' + exploreData.subcategories.join(', ');
              }
              handleReadAloud(fullText, 'explore');
            }} style={{cursor: 'pointer'}}>
              <img src={isReading && readingId === 'explore' ? "/images/muteicon.svg" : "/images/blackreadaloud.svg"} alt="" width="16" height="16" />
              <span>{isReading && readingId === 'explore' ? 'Stop' : 'Say It Aloud'}</span>
            </div>
          </div>
            </>
          )}
          {/* Conversation Messages */}
          {messages.map((msg, i) => (
            msg.role === 'user' ? (
              <div key={i} className="ci-msg-user">
                <div className="ci-msg-user-content">
                  <span className="ci-msg-user-text">{msg.text}</span>
                </div>
                <div className="ci-msg-user-avatar"><span>{getUserInitials(user)}</span></div>
              </div>
            ) : msg.loading ? (
              <div key={i} className="ci-msg-bot" ref={i === messages.length - 1 ? latestBotRef : null}>
                <div className="ci-bot-avatar"><span>LB</span></div>
                <div className="ci-msg-bot-body">
                  <div className="ci-typing"><span></span><span></span><span></span></div>
                </div>
              </div>
            ) : msg.role === 'disclaimer' ? (
              <div key={i} className="ci-inline-disclaimer">
                <div className="ci-bot-avatar"><span>LB</span></div>
                <div className="ci-inline-disclaimer-body">
                  <p className="ci-inline-disclaimer-title">A note before you begin</p>
                  <p className="ci-inline-disclaimer-text">These cultural insights are intended to provide general guidance based on commonly observed cultural tendencies and majority perspectives. They are not meant to stereotype individuals, define all members of a culture, or suggest that one culture is better than another. Individual experiences, behaviors, and perspectives may vary, and users are encouraged to approach the content with openness and cultural sensitivity</p>
                  <div className="ci-inline-disclaimer-footer">
                    <span className="ci-inline-disclaimer-note"><img src="/images/disclaimericon.svg" alt="" width="14" height="14" /> Shown once when country is changed.</span>
                    <div className="ci-inline-disclaimer-actions">
                      <label className="ci-check-label">
                        <input type="checkbox" checked={inlineDisclaimerChecked} onChange={(e) => setInlineDisclaimerChecked(e.target.checked)} />
                        <span>I Understand</span>
                      </label>
                      <button className="ci-inline-disclaimer-btn" disabled={!inlineDisclaimerChecked} onClick={() => {
                        // Accept disclaimer
                        axios.post(`${API_BASE_URL}/cultural-insights/disclaimer/accept`, { country: msg.country }, { withCredentials: true }).catch(() => {});
                        
                        if (msg.pendingExplore) {
                          // Country change — fetch flag first, then show response inline
                          setCurrentCategory(msg.pendingExplore.category || null);
                          setActiveCountry(msg.country);
                          axios.get(`${API_BASE_URL}/cultural-insights/countries`, { withCredentials: true })
                            .then(cRes => {
                              let flagUrl = activeFlag;
                              if (cRes.data.success) {
                                const found = cRes.data.countries.find(c => c.country_name === msg.country);
                                if (found?.flag_url) { flagUrl = found.flag_url; setActiveFlag(found.flag_url); }
                              }
                              setMessages(prev => {
                                const filtered = prev.filter(m => m.role !== 'disclaimer');
                                return [...filtered, { 
                                  role: 'bot', 
                                  isCountryChange: true,
                                  countryData: msg.pendingExplore,
                                  summary: msg.pendingExplore.welcome_message || msg.response || '',
                                  topics: msg.pendingExplore.subcategories || [],
                                  msgCountry: msg.country,
                                  msgCategory: msg.pendingExplore.category || null,
                                  flagUrl: flagUrl
                                }];
                              });
                            }).catch(() => {
                              setMessages(prev => {
                                const filtered = prev.filter(m => m.role !== 'disclaimer');
                                return [...filtered, { 
                                  role: 'bot', 
                                  isCountryChange: true,
                                  countryData: msg.pendingExplore,
                                  summary: msg.pendingExplore.welcome_message || msg.response || '',
                                  topics: msg.pendingExplore.subcategories || [],
                                  msgCountry: msg.country,
                                  msgCategory: msg.pendingExplore.category || null,
                                  flagUrl: activeFlag
                                }];
                              });
                            });
                        } else if (msg.pendingAction) {
                          // Initial page load disclaimer — proceed with agent call
                          setMessages([]);
                          if (msg.pendingAction === 'category' && msg.pendingCategory) {
                            setCurrentCategory(msg.pendingCategory);
                            callAgent('category', msg.pendingCategory, true);
                          } else {
                            callAgent('explore', null);
                          }
                        }
                        setInlineDisclaimerChecked(false);
                      }}>Continue</button>
                    </div>
                  </div>
                </div>
              </div>
            ) : msg.isCountryChange ? (
              <div key={i} className="ci-msg-bot ci-country-change-msg" ref={i === messages.length - 1 ? latestBotRef : null}>
                <div className="ci-bot-avatar"><span>LB</span></div>
                <div className="ci-msg-bot-body">
                  <div className="ci-explore-header" style={{marginBottom: '8px'}}>
                    <img src={msg.flagUrl || activeFlag} alt="" className="ci-explore-flag" />
                    <h2 className="ci-explore-title">{msg.countryData?.country} Life: <span style={{fontWeight: 100, fontFamily: 'Graphik-Regular'}}>{msg.countryData?.category || 'Explore All'}</span></h2>
                  </div>
                  {msg.countryData?.country_info && (
                    <div className="ci-explore-meta" style={{marginLeft: 0}}>
                      <span>Capital: <strong>{msg.countryData.country_info.capital}</strong></span>
                      <span>Language(s): <strong>{msg.countryData.country_info.language}</strong></span>
                      <span>Time Zone: <strong>{msg.countryData.country_info.timezone}</strong></span>
                      <span>Currency: <strong>{msg.countryData.country_info.currency}</strong></span>
                    </div>
                  )}
                  <div className="ci-explore-divider"></div>
                  <p className="ci-msg-bot-summary">{msg.summary}</p>
                  {msg.topics?.length > 0 && (
                    <div className="ci-msg-bot-topics">
                      {msg.topics.map((topic, ti) => (
                        <div key={ti} className={`ci-msg-topic-row${detailPanel?.topic === topic ? ' active' : ''}`}>
                          <span className="ci-msg-topic-name">{topic}</span>
                          <a className="ci-msg-topic-link" onClick={() => handleViewDetails(topic, msg.msgCountry, msg.msgCategory)}>View details</a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div key={i} className="ci-msg-bot" ref={i === messages.length - 1 ? latestBotRef : null}>
                <div className="ci-bot-avatar"><span>LB</span></div>
                <div className="ci-msg-bot-body">
                  <p className="ci-msg-bot-summary">{msg.summary}</p>
                  <div className="ci-msg-bot-topics">
                    {msg.topics?.map((topic, ti) => (
                      <div key={ti} className={`ci-msg-topic-row${detailPanel?.topic === topic ? ' active' : ''}`}>
                        <span className="ci-msg-topic-name">{topic}</span>
                        <a className="ci-msg-topic-link" onClick={() => handleViewDetails(topic, msg.msgCountry, msg.msgCategory)}>View details</a>
                      </div>
                    ))}
                  </div>
                  <div className="ci-say-aloud" style={{marginTop: '12px', cursor: 'pointer'}} onClick={() => {
                    let fullText = msg.summary || '';
                    if (msg.topics?.length > 0) fullText += '. Topics: ' + msg.topics.join(', ');
                    handleReadAloud(fullText, 'msg_' + i);
                  }}>
                    <img src={isReading && readingId === 'msg_' + i ? "/images/muteicon.svg" : "/images/blackreadaloud.svg"} alt="" width="16" height="16" />
                    <span>{isReading && readingId === 'msg_' + i ? 'Stop' : 'Say It Aloud'}</span>
                  </div>
                </div>
              </div>
            )
          ))}
          <div ref={messagesEndRef} />

          <div className="ci-collapse-wrapper ci-landing-collapse" ref={categoriesPanelRef}>
            <button className="ci-collapse-icon" onMouseEnter={() => { if (!showCategoriesPanel) setShowCollapseTooltip(true); }} onMouseLeave={() => setShowCollapseTooltip(false)} onClick={() => { setShowCategoriesPanel(!showCategoriesPanel); setShowCollapseTooltip(false); }}>
              <img src="/images/CollapseIcon.svg" alt="" />
            </button>
            {showCollapseTooltip && !showCategoriesPanel && (
              <div className="ci-collapse-label">
                <img src="/images/TooltipBubble.svg" alt="" className="ci-collapse-bubble" />
                <span className="ci-collapse-text">Explore<br/>Categories</span>
              </div>
            )}
            {showCategoriesPanel && (
              <div className="ci-landing-categories-panel">
                <div className="ci-landing-panel-header" onClick={() => setShowCategoriesPanel(false)}>
                  <span>Category</span>
                  <img src="/images/purplearrow.svg" alt="" width="12" height="12" />
                </div>
                {(exploreData?.categories || categories).map((cat, i) => {
                  const catName = cat.name || cat.title;
                  const catIcon = getPurpleCategoryIcon(catName);
                  return (
                    <div key={i} className="ci-landing-panel-item" onClick={() => { 
                      setShowCategoriesPanel(false);
                      setCurrentCategory(catName);
                      handleCategoryQuery(catName);
                    }}>
                      <img src={catIcon} alt="" width="20" height="20" />
                      <span>{catName}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="ci-chat-input-section">
          {showPromptsPopup && (
            <div className="ci-prompts-popup">
              <div className="ci-prompts-popup-header">
                <span>Explore prompt library</span>
                <img src="/images/Close-cancel.svg" alt="" width="16" height="16" style={{cursor: 'pointer'}} onClick={() => setShowPromptsPopup(false)} />
              </div>
              <div className="ci-prompts-popup-list">
                {categoryPrompts.map((p, i) => (
                  <div key={i} className={`ci-prompts-popup-item${waitingForResponse ? ' disabled' : ''}`} onClick={() => handlePromptClick(p.prompt_text)} style={waitingForResponse ? {opacity: 0.5, pointerEvents: 'none'} : {}}>
                    {p.prompt_text}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="ci-chat-input-bar" style={waitingForResponse ? {opacity: 0.4} : {}}>
            <textarea
              ref={inputRef}
              className="ci-chat-input"
              placeholder={`Ask anything about ${activeCountry} culture`}
              value={inputText}
              rows={1}
              disabled={waitingForResponse}
              onChange={(e) => { setInputText(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; e.target.style.overflowY = e.target.scrollHeight > 120 ? 'auto' : 'hidden'; }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && inputText.trim() && !waitingForResponse) { e.preventDefault(); handleSend(); } }}
            />
            <span className="ci-prompts-btn" onClick={handleTogglePrompts} style={waitingForResponse ? {opacity: 0.4, pointerEvents: 'none'} : {}}><img src="/images/Group.svg" alt="" width="12" height="15" /> Prompts</span>
            <button className={`voice-button ${sttActive ? 'recording' : ''}`} onClick={handleMicClick} disabled={waitingForResponse && !sttActive} style={waitingForResponse && !sttActive ? {opacity: 0.4} : {}}><img src="/images/micicon.png" alt="Mic" width="20" height="20" /></button>
            <button className="ci-chat-send" onClick={handleSend} disabled={!inputText.trim() || waitingForResponse} style={{background: inputText.trim() ? '#A100FF' : 'transparent', opacity: waitingForResponse ? 0.4 : 1}}>
              <img src={inputText.trim() ? "/images/activeSend.svg" : "/images/sendicon.png"} alt="Send" width="20" height="20" />
            </button>
          </div>
          <div className="ci-chat-disclaimer">
            <span>Response generated by AI. Verify accuracy and privacy restrictions before use.</span>
            <div className="ci-disclaimer-links">
              <button className="disclaimer-link" onClick={() => setShowTermsModal(true)}>Terms of use</button>
              <button className="disclaimer-link" onClick={() => setShowPrivacyModal(true)}>Privacy policy</button>
            </div>
          </div>
        </div>
      </main>

      {/* Detail Panel - Right Side Drawer */}
      {detailPanel && (
        <div className="ci-detail-panel">
          <div className="ci-detail-header">
            <h3 className="ci-detail-title">{detailPanel.topic}</h3>
            <div className="ci-detail-header-actions">
              <div className="ci-say-aloud" style={{cursor: 'pointer'}} onClick={() => {
                let fullText = detailPanel.description || '';
                if (detailPanel.tips) {
                  fullText += '. Tips: ' + (Array.isArray(detailPanel.tips) ? detailPanel.tips.join('. ') : detailPanel.tips);
                }
                if (detailPanel.example) {
                  const exText = typeof detailPanel.example === 'object' ? detailPanel.example.text : detailPanel.example;
                  if (exText) fullText += '. Example: ' + exText;
                }
                handleReadAloud(fullText, 'detail');
              }}>
                <img src={isReading && readingId === 'detail' ? "/images/muteicon.svg" : "/images/blackreadaloud.svg"} alt="" width="18" height="15" />
                <span>{isReading && readingId === 'detail' ? 'Stop' : 'Say It Aloud'}</span>
              </div>
              <button className="ci-detail-close" onClick={() => { detailClosedRef.current = true; setDetailPanel(null); }}>
                <img src="/images/Close-cancel.svg" alt="Close" width="20" height="20" />
              </button>
            </div>
          </div>
          <div className="ci-detail-body">
            {detailPanel.loading ? (
              <div className="ci-loading-container">
                <div className="ci-typing"><span></span><span></span><span></span></div>
              </div>
            ) : (
              <>
                <div className="ci-detail-content-row">
                  <div className="ci-detail-text-col">
                    <p className="ci-detail-description">{detailPanel.description}</p>
                  </div>
                  <img src={detailPanel.image || "/images/Communicationillustration.svg"} alt="" className="ci-detail-illustration" />
                </div>
                {detailPanel.tips && (
                  <div className="ci-detail-tips">
                    <p className="ci-detail-tips-label">Tips:</p>
                    {Array.isArray(detailPanel.tips) ? (
                      <ul className="ci-detail-tips-list">
                        {detailPanel.tips.map((tip, i) => <li key={i}>{tip}</li>)}
                      </ul>
                    ) : (
                      <p className="ci-detail-tips-text">{detailPanel.tips}</p>
                    )}
                  </div>
                )}
                {detailPanel.example && (
                  <div className="ci-detail-example">
                    <p className="ci-detail-example-label">{typeof detailPanel.example === 'object' ? detailPanel.example.label || 'Example:' : 'Example:'}</p>
                    <p className="ci-detail-example-text">{typeof detailPanel.example === 'object' ? detailPanel.example.text : detailPanel.example}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <Footer />

      {showPrivacyModal && (
        <div className="modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="footer-privacy-content">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Privacy Policy</h2>
                <button className="modal-close" onClick={() => setShowPrivacyModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <h3>Data Privacy Statement</h3>
                <p>Please be aware that any personal data that you and others provide through this tool may be processed by Accenture.</p>
                <p>Such personal data will be processed in accordance with Accenture's <a href="https://www.accenture.com/us-en/about/privacy-policy" target="_blank" rel="noopener noreferrer">Global Data Privacy Policy</a>.</p>
                <p>Before providing any personal data through this tool, Accenture invites you to read its <a href="https://www.accenture.com/us-en/about/privacy-policy" target="_blank" rel="noopener noreferrer">privacy statement</a>, which includes important information on why and how Accenture is processing your personal data.</p>
              </div>
              <div className="modal-footer">
                <button className="modal-agree-button" onClick={() => setShowPrivacyModal(false)}>I Agree</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTermsModal && (
        <div className="modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="footer-term-content">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Terms of Use</h2>
                <button className="modal-close" onClick={() => setShowTermsModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <h3>Acceptance of Terms</h3>
                <p>By accessing and using Language Buddy, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.</p>
                <h3>License and Access</h3>
                <p>Language Buddy grants you a limited, non-exclusive, non-transferable license to access and use the platform for personal language learning purposes.</p>
                <h3>User Responsibilities</h3>
                <p>You agree to use the platform responsibly and in accordance with all applicable laws and regulations.</p>
                <h3>Intellectual Property</h3>
                <p>All content, features, and functionality of Language Buddy are owned by Accenture and are protected by international copyright, trademark, and other intellectual property laws.</p>
              </div>
              <div className="modal-footer">
                <button className="modal-agree-button" onClick={() => setShowTermsModal(false)}>I Agree</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CulturalInsightsChat;
